import { useState } from 'react';
import { CheckCircle2, Circle, Lock, AlertTriangle, Loader2, FileClock } from 'lucide-react';
import { vatReportingApi } from '../../services/vatReportingApi';
import { useVatLang } from './VatLang';
import type { PeriodStatus, VatCompany, VatReturnResponse } from '../../types/vatReporting';

/**
 * open -> reviewed -> filed, and what filing means.
 *
 * Marking a period FILED freezes every transaction that went into it. That is the whole point
 * of the status: from then on the page can say which document arrived late, not merely that a
 * figure moved. The button says so, because a user who does not know that will not use it.
 */

interface Props {
    data: VatReturnResponse;
    company: VatCompany;
    year: number;
    month: number;
    canEdit: boolean;
    onChanged: () => void;
    onError: (message: string) => void;
}

/** `label` is the English fallback; `labelKey` is what the interface actually renders. */
const STEPS: { key: PeriodStatus; label: string; labelKey: string; hint: string }[] = [
    { key: 'open', label: 'Open', labelKey: 'status.open', hint: 'Figures are live; nothing is frozen.' },
    { key: 'reviewed', label: 'Reviewed', labelKey: 'status.reviewed', hint: 'Checked, not yet transmitted.' },
    { key: 'filed', label: 'Filed', labelKey: 'status.filed', hint: 'Transmitted to ELSTER; transactions frozen.' },
];

export function FilingStatusBar({ data, company, year, month, canEdit, onChanged, onError }: Props) {
    const { t } = useVatLang();
    const status: PeriodStatus = data.period?.status ?? 'open';
    /**
     * A backfilled month. Marked everywhere it could otherwise be mistaken for a real freeze:
     * the figures are the transmitted ones, the transaction list behind them is not the one
     * that was on screen when Heike filed. Saying "filed" without saying this would be the
     * page claiming an audit trail it does not have.
     */
    const reconstructed = data.period?.snapshotOrigin === 'reconstructed';
    const [busy, setBusy] = useState(false);
    const [showFileForm, setShowFileForm] = useState(false);
    const [ticket, setTicket] = useState(data.period?.transferTicket ?? '');
    const [zmTicket, setZmTicket] = useState(data.period?.ecSalesTransferTicket ?? '');
    const [note, setNote] = useState(data.period?.note ?? '');

    const blockingErrors = data.checks.filter(c => c.status === 'error');

    async function move(next: PeriodStatus, acknowledgeIncompleteData = false) {
        setBusy(true);
        try {
            await vatReportingApi.setStatus({
                company, year, month, status: next,
                transferTicket: next === 'filed' ? (ticket.trim() || null) : null,
                ecSalesTransferTicket: next === 'filed' ? (zmTicket.trim() || null) : null,
                note: note.trim() || null,
                acknowledgeIncompleteData,
            });
            setShowFileForm(false);
            onChanged();
        } catch (e) {
            const message = e instanceof Error ? e.message : t('status.changeFailed');
            // The server refuses to file on a month the replica has not finished loading. That
            // is a decision for the user to override in the open, not something to retry behind
            // their back.
            if (message.includes('acknowledgeIncompleteData')) {
                if (window.confirm(`${message}\n\n${t('status.fileAnyway')}`)) {
                    await move(next, true);
                    return;
                }
            } else {
                onError(message);
            }
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="border rounded-lg bg-white">
            <div className="flex flex-wrap items-center gap-6 px-4 py-3">
                <div className="flex items-center gap-4">
                    {STEPS.map((s, i) => {
                        const reached = STEPS.findIndex(x => x.key === status) >= i;
                        const current = s.key === status;
                        return (
                            <div key={s.key} className="flex items-center gap-1.5" title={s.hint}>
                                {s.key === 'filed' && reached
                                    ? <Lock className="h-4 w-4 text-green-600" />
                                    : reached
                                        ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                                        : <Circle className="h-4 w-4 text-gray-300" />}
                                <span className={`text-sm ${current ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
                                    {t(s.labelKey)}
                                </span>
                            </div>
                        );
                    })}
                    {reconstructed && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-amber-300
                                         bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
                            <FileClock className="h-3.5 w-3.5" />
                            {t('status.reconstructedBadge')}
                        </span>
                    )}
                </div>

                <div className="text-xs text-gray-500 flex-1 min-w-[16rem]">
                    {status === 'filed' && reconstructed && (
                        <>
                            {t('status.reconstructedOn', {
                                date: data.period?.filedAt
                                    ? new Date(data.period.filedAt).toLocaleDateString('de-CH') : '—',
                            })}
                            {data.period?.transferTicket && (
                                <> · {t('status.ticket')} <code>{data.period.transferTicket}</code></>
                            )}
                        </>
                    )}
                    {status === 'filed' && !reconstructed && data.period?.filedAt && (
                        <>
                            {t('status.filedBy', {
                                date: new Date(data.period.filedAt).toLocaleDateString('de-CH'),
                                user: data.period.filedBy ?? '—',
                            })}
                            {data.period.transferTicket && (
                                <> · {t('status.ticket')} <code>{data.period.transferTicket}</code></>
                            )}
                            {data.period.snapshotEntryCount !== null && (
                                <> · {t('status.frozenCount', { count: data.period.snapshotEntryCount })}</>
                            )}
                        </>
                    )}
                    {status === 'reviewed' && data.period?.reviewedAt && (
                        <>{t('status.reviewedBy', {
                            date: new Date(data.period.reviewedAt).toLocaleDateString('de-CH'),
                            user: data.period.reviewedBy ?? '—',
                        })}</>
                    )}
                    {status === 'open' && t('status.nothingFrozen')}
                </div>

                {canEdit && (
                    <div className="flex items-center gap-2">
                        {busy && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
                        {status === 'open' && (
                            <button onClick={() => move('reviewed')} disabled={busy}
                                    className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50 disabled:opacity-50">
                                {t('status.markReviewed')}
                            </button>
                        )}
                        {status !== 'filed' && (
                            <button onClick={() => setShowFileForm(v => !v)} disabled={busy}
                                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                                {t('status.markFiled')}
                            </button>
                        )}
                        {status === 'filed' && (
                            <button onClick={() => move('reviewed')} disabled={busy}
                                    className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50 disabled:opacity-50"
                                    title={t('status.reopenHint')}>
                                {t('status.reopen')}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {reconstructed && (
                <div className="border-t border-amber-200 bg-amber-50 px-4 py-3">
                    <div className="flex items-start gap-2">
                        <FileClock className="h-4 w-4 mt-0.5 shrink-0 text-amber-700" />
                        <div className="text-xs text-amber-900 space-y-1">
                            <p className="font-medium">{t('status.reconstructedTitle')}</p>
                            <p>{t('status.reconstructedBody')}</p>
                            {data.period?.filedSourceRef && (
                                <p className="text-amber-800">
                                    {t('status.reconstructedSource')}:{' '}
                                    <code className="break-all">{data.period.filedSourceRef}</code>
                                </p>
                            )}
                            {data.period?.snapshotEntryCount !== null
                                && data.period?.snapshotEntryCount !== undefined && (
                                <p className="text-amber-800">
                                    {t('status.frozenCount', { count: data.period.snapshotEntryCount })}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showFileForm && canEdit && (
                <div className="border-t px-4 py-3 bg-gray-50 space-y-3">
                    {blockingErrors.length > 0 && (
                        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                            <span>{t('status.stillFailing', { count: blockingErrors.length })}</span>
                        </div>
                    )}
                    <p className="text-sm text-gray-600">
                        {t('status.freezeExplainer', {
                            count: data.entryCount,
                            period: `${year}-${String(month).padStart(2, '0')}`,
                        })}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <label className="text-sm">
                            <span className="block text-gray-600 mb-1">{t('status.ticketUstva')}</span>
                            <input value={ticket} onChange={e => setTicket(e.target.value)}
                                   placeholder="ep2326qh6v8r0m23e1fv6do1phyzc2wf"
                                   className="w-full border rounded px-2 py-1 font-mono text-xs" />
                        </label>
                        <label className="text-sm">
                            <span className="block text-gray-600 mb-1">{t('status.ticketZm')}</span>
                            <input value={zmTicket} onChange={e => setZmTicket(e.target.value)}
                                   placeholder={t('status.optional')}
                                   className="w-full border rounded px-2 py-1 font-mono text-xs" />
                        </label>
                        <label className="text-sm">
                            <span className="block text-gray-600 mb-1">{t('status.note')}</span>
                            <input value={note} onChange={e => setNote(e.target.value)}
                                   className="w-full border rounded px-2 py-1 text-xs" />
                        </label>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => move('filed')} disabled={busy}
                                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                            {t('status.freezeAndFile')}
                        </button>
                        <button onClick={() => setShowFileForm(false)}
                                className="px-3 py-1.5 text-sm border rounded hover:bg-white">
                            {t('status.cancel')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
